import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {Employee} from 'src/app/views/wrapper/payroll/employee/employee';
import {environment} from 'src/environments/environment';
import {IResponse} from '../service';

@Injectable({
  providedIn: 'root'
})
export class EmployeeService {

  employeeDataSubject = new BehaviorSubject<boolean>(null);

  constructor(
    private http: HttpClient
  ) {
  }

  createEmployees(body: Employee[], companyId: string): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/companies/${companyId}/employees`, {
      employees: body
    });
  }

  getAllEmployees(companyId: string): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/companies/${companyId}/employees`);
  }

  filterEmployees(companyId: string, body: any): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/companies/${companyId}/employees/filter`, body);
  }

  filterEmployeeCount(companyId: string, body: any): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/companies/${companyId}/employees/filter/count`, body);
  }

  filterCounterEmployees(companyId: string): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/companies/${companyId}/employees/filter/counter`);
  }

  getEmployeeList(companyId: string, employeeList: string[]): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/companies/${companyId}/employees/list`, {
      employees: employeeList
    });
  }

  getEmployeeDeductionList(companyId: string, employeeList: string[], taxYear: string): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/deductions/companies/${companyId}/employees/list`, {
      employees: employeeList,
      taxYear
    });
  }

  getEmployeeDeduction(companyId: string, employeeId: string, taxYear: string): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/deductions/companies/${companyId}/employee/${employeeId}/${taxYear}`);
  }

  getOtherDeductionMetadata(year: string): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/deductions/other/${year}`);
  }

  uploadDeductionFile(companyId: string, deductionID: string, formData: FormData): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/deductions/companies/${companyId}/file/${deductionID}`, formData);
  }

  deleteDeductionFiles(companyId: string, deductionID: string, fileNames: string[]): Observable<any> {
    return this.http.delete(`${environment.apiBaseUrl}/deductions/companies/${companyId}/file/${deductionID}?file_names=${fileNames}`);
  }

  getDeductionPresignFile(companyId: string, deductionID: string, fileName: string): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/deductions/companies/${companyId}/file/${deductionID}/${fileName}`);
  }

  getEmployee(companyId: string, employeeId: string): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/companies/${companyId}/employees/${employeeId}/detail`);
  }

  getEmployeePayrollSummary(companyId: string, employeeId: string, year: string): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/companies/${companyId}/employees/${employeeId}/payroll/${year}`);
  }

  calculatePayrollWage(companyId: string, employeeId: string, params: any): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/companies/${companyId}/employees/${employeeId}/calculate_wage`, {params: params});
  }

  editEmployeeList(body: Employee[], companyId: string): Observable<any> {
    return this.http.put(`${environment.apiBaseUrl}/companies/${companyId}/employees`, {
      employees: body
    });
  }

  editEmployeeAboutList(body: any, companyId: string): Observable<any> {
    return this.http.put(`${environment.apiBaseUrl}/companies/${companyId}/employees/about/list`, {
      data: body
    });
  }

  editEmployeeEmploymentList(body: any, companyId: string): Observable<any> {
    return this.http.put(`${environment.apiBaseUrl}/companies/${companyId}/employees/employment/list`, {
      data: body
    });
  }

  editEmployeeContactList(body: any, companyId: string): Observable<any> {
    return this.http.put(`${environment.apiBaseUrl}/companies/${companyId}/employees/contact/list`, body);
  }

  editEmployeeAbout(body: any, companyId: string, employeeId: string): Observable<any> {
    return this.http.put(`${environment.apiBaseUrl}/companies/${companyId}/employees/${employeeId}/about`, body);
  }

  editEmployeeEmployment(body: any, companyId: string, employeeId: string): Observable<any> {
    return this.http.put(`${environment.apiBaseUrl}/companies/${companyId}/employees/${employeeId}/employment`, body);
  }

  editEmployeeContact(body: any, companyId: string, employeeId: string): Observable<any> {
    return this.http.put(`${environment.apiBaseUrl}/companies/${companyId}/employees/${employeeId}/contact`, body);
  }

  editEmployeeDeductions(body: any, companyId: string, taxYear: string): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/deductions/companies/${companyId}`, {
      data: body,
      taxYear
    });
  }

  deleteEmployee(employeeId: string, companyId: string): Observable<any> {
    return this.http.delete(`${environment.apiBaseUrl}/companies/${companyId}/employees/${employeeId}`);
  }

  updateEmployeeEndDate(body: any, companyId: string, employeeId: string): Observable<any> {
    return this.http.put(`${environment.apiBaseUrl}/companies/${companyId}/employees/${employeeId}/end_date`, body);
  }

  calculateEmployeeEndDate(body: any, companyId: string, employeeId: string): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/companies/${companyId}/employees/${employeeId}/end_date/calculate`, body);
  }

  checkLinkToken(body: any, companyId: string, employeeId: string, payrollId: string): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/employeeemail/companies/${companyId}/payroll/${payrollId}/employee/${employeeId}/paysliptoken`, body);
  }

  confirmSendPayslip(body: any, companyId: string, employeeId: string, payrollId: string): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/employeeemail/companies/${companyId}/payroll/${payrollId}/employee/${employeeId}/employeepayslip`, body);
  }

  uploadEmployeeDocument(body: any): Observable<any> {
    const formData = new FormData();
    formData.append('file', body.file);
    return this.http.put(`${environment.apiBaseUrl}/companies/${body.companyId}/employees/${body.employeeId}/${body.type}`, formData);
  }

  deleteEmployeeDocument(body: any): Observable<any> {
    return this.http.delete(`${environment.apiBaseUrl}/companies/${body.companyId}/employees/${body.employeeId}/${body.type}`);
  }

  getDocumentByEmpIdAndYear(body: any): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/companies/${body.companyId}/employees/${body.employeeId}/document/${body.year}`);
  }

  getViewFileUrl(body: any): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/companies/${body.companyId}/employees/${body.employeeId}/documents/${body.type}`);
  }

  getCompanyGuarantor(body: any): Observable<any> {
    if (body.type) {
      if (body.employeeId) {
        return this.http.get(`${environment.apiBaseUrl}/companies/${body.companyId}/guarantor/employee/${body.employeeId}/${body.type}`);
      } else {
        return this.http.get(`${environment.apiBaseUrl}/companies/${body.companyId}/guarantor/${body.type}`);
      }
    } else {
      return this.http.get(`${environment.apiBaseUrl}/companies/${body.companyId}/guarantor`);
    }
  }

  upsertGuarantor(body: any): Observable<any> {
    if (body.type) {
      return this.http.post(`${environment.apiBaseUrl}/companies/${body.companyId}/guarantor/${body.type}`, body);
    } else {
      return this.http.post(`${environment.apiBaseUrl}/companies/${body.companyId}/guarantor`, body);
    }
  }

  getEmployeeNoByCompanyId(companyId: string): Observable<IResponse> {
    return this.http.get<IResponse>(`${environment.apiBaseUrl}/companies/${companyId}/employees/employmentids/list`);
  }

  upsertCompensation(body: any): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/companies/${body.companyId}/employees/${body.employeeId}/compensation`, body);
  }

  deleteCompensation(body: any): Observable<any> {
    return this.http.delete(`${environment.apiBaseUrl}/companies/${body.companyId}/employees/${body.employeeId}/compensation/${body.id}`);
  }

  upsertOrganisation(body: any): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/companies/${body.companyId}/employees/${body.employeeId}/organisation`, body);
  }

  upsertSupervisor(body: any): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/companies/${body.companyId}/employees/${body.employeeId}/supervisor`, body);
  }

  getSupervisorAllowType(companyId: string): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/companies/${companyId}/employees/supervisor_allow_type`);
  }

  getAbsenceType(companyId: string): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/companies/${companyId}/employees/absence_type`);
  }

  updateAbsence(body: any): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/companies/${body.companyId}/employees/${body.employeeId}/absence`, body);
  }

  getEmployeeAbsenceHolidayList(companyId: string, employeeList: string[], year: number): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/companies/${companyId}/employees/holiday`, {employee_ids: employeeList, year});
  }

  getEmployeeAbsenceOtherList(companyId: string, employeeList: string[], year: number): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/companies/${companyId}/employees/absence`, {employee_ids: employeeList, year});
  }

  updateEmployeeAbsenceHolidayList(companyId: string, list: any[], year: number): Observable<any> {
    return this.http.put(`${environment.apiBaseUrl}/companies/${companyId}/employees/holiday`, {list, year});
  }

  updateEmployeeAbsenceOtherList(companyId: string, list: any[], year: number): Observable<any> {
    return this.http.put(`${environment.apiBaseUrl}/companies/${companyId}/employees/absence`, {list, year});
  }

  getEmployeeLeaveNotifications(companyId: string, employeeId: string, year: string): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/companies/${companyId}/employees/${employeeId}/leave_noti/${year}`);
  }

  updateEmployeeWorkingCalendars(companyId: string, year: any, body: any): Observable<any> {
    if (year) {
      return this.http.patch<IResponse>(`${environment.apiBaseUrl}/companies/${companyId}/employees/employee_working_calendars/${year}`, null);
    } else {
      return this.http.patch<IResponse>(`${environment.apiBaseUrl}/companies/${companyId}/employees/employee_working_calendars`, body);
    }
  }

  getEmployeesTimeClocks(companyId: string, body: any): Observable<any> {
    return this.http.post<IResponse>(`${environment.apiBaseUrl}/companies/${companyId}/employees/employee_time_clocks`, body);
  }
  getEmployeesWorkingCalendars(companyId: string, body: any): Observable<any> {
    return this.http.post<IResponse>(`${environment.apiBaseUrl}/companies/${companyId}/employees/employee_working_calendars`, body);
  }
  getEmployeesAbsences(companyId: string, body: any): Observable<any> {
    return this.http.post<IResponse>(`${environment.apiBaseUrl}/companies/${companyId}/employees/employee_absences`, body);
  }
  getEmployeesLeaves(companyId: string, body: any): Observable<any> {
    return this.http.post<IResponse>(`${environment.apiBaseUrl}/companies/${companyId}/employees/employee_notification_leaves`, body);
  }
  getEmployeesTimeClockConfirmations(companyId: string, body: any): Observable<any> {
    return this.http.post<IResponse>(`${environment.apiBaseUrl}/companies/${companyId}/employees/employee_time_clock_confirmations`, body);
  }

  updateEmployeeTimeClockConfirmations(companyId: string, employeeId: string, body: any): Observable<any> {
    return this.http.post<IResponse>(`${environment.apiBaseUrl}/companies/${companyId}/employees/${employeeId}/employee_time_clock_confirmations`, body);
  }

  getEmployeeWorkingHours(companyId: string, employeeId: string, params: any): Observable<any> {
    return this.http.get<IResponse>(`${environment.apiBaseUrl}/companies/${companyId}/employees/${employeeId}/employee_working_hour`, { params:params });
  }
  getEmployeeClockIn(companyId: string, employeeId: string, params: any): Observable<any> {
    return this.http.get<IResponse>(`${environment.apiBaseUrl}/companies/${companyId}/employees/${employeeId}/employee_clock_in`, { params:params });
  }
  getEmployeePayCut(companyId: string, employeeId: string, params: any): Observable<any> {
    return this.http.get<IResponse>(`${environment.apiBaseUrl}/companies/${companyId}/employees/${employeeId}/employee_pay_cut`, { params:params });
  }
  // Self Onboarding
  createSelfOnboardingEmployees(body: Employee[], documentSetting: any, companyId: string): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/companies/${companyId}/employees/self_onboarding`, {
      employees: body,
      document_setting: documentSetting
    });
  }
  getEmployeeSelfOnboardingCode(companyId: string, employeeId: string): Observable<any> {
    return this.http.get<IResponse>(`${environment.apiBaseUrl}/companies/${companyId}/employees/${employeeId}/employee_self_onboarding_code`);
  }
  renewEmployeeSelfOnboardingCode(companyId: string, employeeId: string): Observable<any> {
    return this.http.post<IResponse>(`${environment.apiBaseUrl}/companies/${companyId}/employees/${employeeId}/employee_self_onboarding_code`, null);
  }
  setSelfOnboardStatusCompleted(companyId: string, employeeId: string): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/companies/${companyId}/employees/${employeeId}/setStatusCompleted`, null);
  }

  getAbsenceReport(companyId: string, year: string): Observable<IResponse> {
    return this.http.get<IResponse>(`${environment.apiBaseUrl}/companies/${companyId}/employees/absence/${year}/report`);
  }
}
